import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { telechargerExportRemunerations, useRemunerations, useStructures } from '@/api/hooks';
import { LIBELLE_STATUT_REMUNERATION, type StatutRemuneration } from '@/api/types';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { StatutRemunerationBadge } from '@/components/paie';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, montant } from '@/lib/utils';

const usd = (n: number | null | undefined) => montant(n, true);

/** Fiches de rémunération de référence des agents (lot HP-2) : statut, anomalies, classe, brut de référence, prochain anniversaire ; ouvre l'onglet Rémunération du dossier HRMS. */
export function Remunerations() {
  const [params, setParams] = useSearchParams();
  const f = { structure: params.get('structure') ?? '', statut: params.get('statut') ?? '', q: params.get('q') ?? '', anomalies: params.get('anomalies') ?? '', anniversaires: params.get('anniversaires') ?? '' };
  const changer = (cle: string, valeur: string) => {
    const n = new URLSearchParams(params);
    if (valeur) n.set(cle, valeur);
    else n.delete(cle);
    setParams(n, { replace: true });
  };
  const structures = useStructures();
  const { data, isLoading, error } = useRemunerations({ structure: f.structure || undefined, statut: f.statut || undefined, q: f.q || undefined, anomalies: f.anomalies || undefined, anniversaires: f.anniversaires || undefined });
  const compteur = (s: StatutRemuneration) => data?.filter((r) => r.statut === s).length ?? 0;
  return (
    <div className="space-y-6">
      <PageHeader
        titre="Fiches de rémunération de référence"
        sousTitre="Barème appliqué, annuités d'ancienneté (classes A et B, cadres de direction), statut de contrôle ; hors présences et retenues."
        actions={
          <Button variant="outline" onClick={() => telechargerExportRemunerations(f).catch((e) => toast.error(e instanceof Error ? e.message : 'Export impossible.'))}>
            Exporter (Excel)
          </Button>
        }
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Fiches" valeur={data?.length ?? 0} detail={`${data?.filter((r) => r.situationValidee).length ?? 0} situation(s) d'ouverture validée(s)`} />
        <Kpi label="À régulariser" valeur={compteur('A_REGULARISER') + compteur('IMPORTEE')} detail="anomalies, annuités ou changements à valider" accent={compteur('A_REGULARISER') > 0 ? 'alerte' : 'ok'} />
        <Kpi label="Contrôlées" valeur={compteur('CONTROLEE')} detail="approbation DRH puis visa DF" />
        <Kpi label="Approuvées" valeur={compteur('APPROUVEE')} detail={`${data?.filter((r) => r.anniversaireProche).length ?? 0} anniversaire(s) dans 30 jours`} accent="ok" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input className="w-64" placeholder="Nom, matricule, grade…" value={f.q} onChange={(e) => changer('q', e.target.value)} />
        <SelectNatif value={f.structure} onChange={(e) => changer('structure', e.target.value)}>
          <option value="">Toutes les structures</option>
          {(structures.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} · {s.libelle}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={f.statut} onChange={(e) => changer('statut', e.target.value)}>
          <option value="">Tous les statuts</option>
          {(Object.keys(LIBELLE_STATUT_REMUNERATION) as StatutRemuneration[]).map((s) => (
            <option key={s} value={s}>
              {LIBELLE_STATUT_REMUNERATION[s]}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={f.anomalies} onChange={(e) => changer('anomalies', e.target.value)}>
          <option value="">Avec ou sans anomalie</option>
          <option value="true">Avec anomalies</option>
          <option value="false">Sans anomalie</option>
        </SelectNatif>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.anniversaires === 'true'} onChange={(e) => changer('anniversaires', e.target.checked ? 'true' : '')} /> Anniversaire dans 30 jours
        </label>
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucune fiche ne correspond." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table data-testid="remunerations">
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Grade → barème</TableHead>
                <TableHead>Classe</TableHead>
                <TableHead className="text-right">Brut du barème</TableHead>
                <TableHead className="hidden lg:table-cell text-right">Annuités</TableHead>
                <TableHead className="text-right">Brut de référence</TableHead>
                <TableHead className="hidden lg:table-cell">Prochain anniversaire</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.agent.id}>
                  <TableCell>
                    <Link to={`/rh/agents/${r.agent.id}/dossier?onglet=remuneration`} className="font-medium text-primary hover:underline">
                      {r.agent.prenom} {r.agent.nom}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {r.agent.matricule} · {r.agent.structure?.code ?? '—'}
                      {r.anomalies ? <span className={r.bloquantes ? ' text-destructive' : ' text-amber-700'}> · {r.anomalies} anomalie(s)</span> : null}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {r.agent.grade ?? '—'}
                    <div className="text-xs text-muted-foreground">{r.ligne ? r.gradeApplique : <span className="text-destructive">sans ligne du barème</span>}</div>
                  </TableCell>
                  <TableCell>{r.cadreDirection ? <Badge variant="outline">cadre</Badge> : r.classe ? <Badge variant="outline">classe {r.classe}</Badge> : '—'}</TableCell>
                  <TableCell className="text-right">{usd(r.brutBareme)}</TableCell>
                  <TableCell className="hidden lg:table-cell text-right">{usd(r.annuitesCumulees)}</TableCell>
                  <TableCell className="text-right font-medium">{usd(r.brutReference)}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {r.prochainAnniversaire ? dateFr(r.prochainAnniversaire) : '—'}
                    {r.anniversaireProche && <Badge variant="secondary" className="ml-1 text-[10px]">bientôt</Badge>}
                  </TableCell>
                  <TableCell>
                    <StatutRemunerationBadge statut={r.statut} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
